/**
 * src/domain/identity/staked-vouch.ts
 *
 * USD-Pegged Staked Vouching Service.
 * - Protects against sybil creation and malicious bot accounts.
 * - Staking is denominated in USD ($50.00 USD target), resolved dynamically via oracle.
 * - Enforces a 90-day maturation lock before bonds can be released.
 * - 50% Slashing Rule: 60% of slashed tokens to victim compensation, 40% to burn pool.
 */

import crypto from 'node:crypto';
import { StakedVouchContract } from './identity-contracts';

export class SovereignVouchService {
  public static readonly TARGET_USD_STAKE = 50.0;
  public static readonly MATURATION_PERIOD_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
  public static readonly MIN_VOUCHER_TRUST_SCORE = 500;
  public static readonly ORACLE_PRICE_FLOOR_USD = 0.10; // Administrative price floor

  private static instance: SovereignVouchService;
  private vouches: Map<string, StakedVouchContract> = new Map();

  private constructor() {}

  public static getInstance(): SovereignVouchService {
    if (!SovereignVouchService.instance) {
      SovereignVouchService.instance = new SovereignVouchService();
    }
    return SovereignVouchService.instance;
  }

  /**
   * Creates a new USD-pegged staked vouch.
   */
  public createVouch(params: {
    voucher: { did: string; trustScore: number; slashed?: boolean };
    voucheeDid: string;
    currentTokenPriceUsd: number;
  }): StakedVouchContract {
    const { voucher, voucheeDid, currentTokenPriceUsd } = params;

    if (voucher.trustScore < SovereignVouchService.MIN_VOUCHER_TRUST_SCORE) {
      throw new Error(
        `Voucher trust score must be >= ${SovereignVouchService.MIN_VOUCHER_TRUST_SCORE} to stake a vouch (current: ${voucher.trustScore}).`
      );
    }
    if (voucher.did === voucheeDid) {
      throw new Error('Self-vouching is cryptographically invalid.');
    }
    if (voucher.slashed) {
      throw new Error('Slashed entities are prohibited from issuing social vouches.');
    }
    if (currentTokenPriceUsd <= 0) {
      throw new Error('Invalid token price from oracle.');
    }

    // Apply administrative price floor to prevent flash-loan / illiquid dump manipulation
    const effectivePrice = Math.max(currentTokenPriceUsd, SovereignVouchService.ORACLE_PRICE_FLOOR_USD);
    const requiredTokens = SovereignVouchService.TARGET_USD_STAKE / effectivePrice;
    const now = Date.now();

    const vouch: StakedVouchContract = {
      id: `vouch_${crypto.randomUUID()}`,
      voucherDid: voucher.did,
      voucheeDid,
      stakedUsdValue: SovereignVouchService.TARGET_USD_STAKE,
      lockedTokens: requiredTokens,
      tokenPriceAtStakeUsd: effectivePrice,
      stakedAtTimestamp: now,
      maturationTimestamp: now + SovereignVouchService.MATURATION_PERIOD_MS,
      status: 'locked',
    };

    this.vouches.set(vouch.id, vouch);
    return vouch;
  }

  /**
   * Applies the 50% slashing rule when a vouchee is convicted of malicious conduct.
   * 60% of the slashed stake is allocated to victim compensation, 40% is burned.
   */
  public applySlashing(vouchId: string, reason: string): {
    slashedStakeTokens: number;
    victimCompensationTokens: number;
    burnedTokens: number;
    vouch: StakedVouchContract;
  } {
    const vouch = this.vouches.get(vouchId);
    if (!vouch) {
      throw new Error(`Vouch record ${vouchId} not found.`);
    }
    if (vouch.status !== 'locked') {
      throw new Error(`Cannot slash vouch with status: ${vouch.status}`);
    }

    const totalSlashed = vouch.lockedTokens * 0.5; // 50% slash rule
    const victimComp = totalSlashed * 0.6;          // 60% to victims
    const burned = totalSlashed * 0.4;              // 40% burned

    vouch.status = 'slashed';
    vouch.slashedDetails = {
      slashedAt: Date.now(),
      totalSlashedTokens: totalSlashed,
      victimCompensationTokens: victimComp,
      burnedTokens: burned,
      reason,
    };

    return {
      slashedStakeTokens: totalSlashed,
      victimCompensationTokens: victimComp,
      burnedTokens: burned,
      vouch,
    };
  }

  /**
   * Releases locked bond tokens if the 90-day maturation period has elapsed.
   */
  public releaseMaturedBond(vouchId: string, currentTimestamp: number = Date.now()): StakedVouchContract {
    const vouch = this.vouches.get(vouchId);
    if (!vouch) {
      throw new Error(`Vouch record ${vouchId} not found.`);
    }
    if (vouch.status !== 'locked') {
      throw new Error(`Vouch is not locked (current status: ${vouch.status}).`);
    }
    if (currentTimestamp < vouch.maturationTimestamp) {
      const remainingDays = Math.ceil((vouch.maturationTimestamp - currentTimestamp) / (24 * 60 * 60 * 1000));
      throw new Error(`Vouch bond has not matured yet. ${remainingDays} days remaining in lock period.`);
    }

    vouch.status = 'matured';
    return vouch;
  }

  public getVouch(vouchId: string): StakedVouchContract | undefined {
    return this.vouches.get(vouchId);
  }

  public getVouchesForVouchee(voucheeDid: string): StakedVouchContract[] {
    return Array.from(this.vouches.values()).filter((v) => v.voucheeDid === voucheeDid);
  }

  public clear(): void {
    this.vouches.clear();
  }
}

export const sovereignVouchService = SovereignVouchService.getInstance();
